import { PhoneIconProps } from "./PhoneIcon";

/**
 * Maps a prospect's `phoneStatus` (+ stored number) to the {@link PhoneIcon} badge — the phone
 * counterpart of `getEmailIconStatus`, and the single source for what a channel badge means:
 *
 * - `checked`   — we hold a number (verified, or stored without a verdict — see {@link hasKnownPhone}).
 * - `failed`    — a lookup ran and found nothing usable.
 * - `searching` — a lookup is in flight.
 * - `refresh`   — nobody has looked yet. Deliberately the default: "not found" and "never tried"
 *   are different answers to the customer, and collapsing them into one badge is how a lead that
 *   was never enriched reads as a lead with no phone.
 *
 * Extracted from `ContactChannels` (PREC-4116) so the read-only surfaces — the lead panel's
 * Profiles cards — badge a colleague exactly as the interactive row badges the open lead.
 */
/**
 * Whether we hold a number for this lead that a click should dial rather than look up again.
 *
 * A stored number is a known number whether or not a provider ever verified it: 691 production
 * prospects carry a phone with no `phoneStatus` at all — imported, typed in, or enriched before
 * the status existed — and requiring `VALID` badged them as "never looked" and turned a click on
 * the icon into a paid lookup for a number already on the record (PREC-4201). Only an explicit
 * `INVALID` verdict, or a lookup in flight, says otherwise.
 */
export const hasKnownPhone = (phone?: string | null, status?: string | null): boolean =>
	knownPhoneNumber(phone, status) !== null;

/** The number to dial when {@link hasKnownPhone} holds, otherwise null. */
export function knownPhoneNumber(phone?: string | null, status?: string | null): string | null {
	const number = phone?.trim();

	return number && status !== "INVALID" && status !== "SEARCHING" ? number : null;
}

export const getPhoneIconStatus = (
	phone?: string | null,
	status?: string | null
): PhoneIconProps["status"] => {
	if (hasKnownPhone(phone, status)) {
		return "checked";
	}

	if (status === "INVALID") {
		return "failed";
	}

	if (status === "SEARCHING") {
		return "searching";
	}

	return "refresh";
};
